import { getContentByUrl } from 'widgets/toolbox/ajax';

/**
 * @typedef {typeof import('widgets/Widget').default} Widget
 * @typedef {InstanceType<typeof import('widgets/toolbox/RefElement').RefElement>} refElement
 */

/**
 * @param {Widget} Widget Base widget for extending
 * @returns {typeof WishListItem} Wishlist item class
 */

export default function (Widget) {
    /**
     * @category widgets
     * @subcategory wishlist
     * @class WishListItem
     * @augments Widget
     * @classdesc Wish list item component with next features:
     * 1. Handles click on product and click on edit button
     * 2. Handles add to cart error
     * 3. Stores actual analytics data
     * @property {string} [addToCartMsg=addToCartMsg] - Reference id for `add to cart message` element
     */
    class WishListItem extends Widget {
        prefs() {
            return {
                addToCartMsg: 'addToCartMsg',
                ...super.prefs()
            };
        }

        /**
         * @description Add global event handlers
         * @returns {void}
         */
        init() {
            this.eventBus().on('product.updated', 'updateGTMInfo');
            this.eventBus().on('product.addedTocart.with.error', 'productAddedToCartWishError');
            super.init();
        }

        /**
         * @description Handler for `edit` product button in a Cart
         * @param {refElement} editBtn - Target `edit` button
         * @listens dom#click
         * @emits WishListItem#edit
         * @emits "product.tile.qv.open.edit"
         * @returns {void}
         */
        editProduct(editBtn) {
            const uuid = editBtn.data('uuid');
            const pid = editBtn.data('pid');
            const qty = editBtn.data('qty');
            const editModeQV = editBtn.data('editModeQv') || false;
            getContentByUrl(editBtn.data('href'), {
                uuid,
                pid,
                qty,
                editModeQV
            })
                .then((response) => {
                    /**
                     * @description Event to show edit button from parent component
                     * @event WishListItem#edit
                     */
                    this.emit('edit', response);
                });
            this.eventBus().emit('product.tile.qv.open.edit', editBtn);
        }

        /**
         * @description Save updated analytics info to property
         * @param {object} response - Response object from server
         * @returns {void}
         */
        updateGTMInfo(response) {
            if (response.success && response.newLineItem) {
                this.currentGtmInfo = response.newLineItem.gtmInfo;
            }
        }

        /**
         * @description Handle clicking on the product link
         * @param {refElement} link - Product link
         * @listens dom#click
         * @emits "wishlist.product.link.click"
         * @returns {void}
         */
        onProductLinkClick(link) {
            this.eventBus().emit('wishlist.product.link.click', link);
        }

        /**
         * @description Error handler on product added to cart
         * @param {object} response - Response object from server
         * @emits "alert.show"
         * @returns {void}
         */
        productAddedToCartWishError(response) {
            const addToCartErrorMessage = response.message;
            if (response.error && addToCartErrorMessage && response.pid) {
                const ref = this.prefs().addToCartMsg;
                this.ref(ref)
                    .setText(addToCartErrorMessage)
                    .show();
            }
            this.eventBus().emit('alert.show', {
                addToCartErrorMessage
            });
        }
    }
    return WishListItem;
}
